import React, { useState } from 'react';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { SlideToggle } from 'ui/SlideToggle';
import Editor from 'pureUi/Editor';
import { UITextArea } from 'ui/UITextArea';
import { Select } from 'ui/Select';
import { MainInfoSection } from './MainInfoSection';
import { ContentDetailsSection } from './ContentDetailsSection';
import { AvailabilityBookingSection } from './AvailabilityBookingSection';
import { PricingInfoSection } from './PricingInfoSection';
import { MediaAssetsSection } from './MediaAssetsSection';
import { SupportingDocumentsSection } from './SupportingDocumentsSection';
import { PublishingSettingsSection } from './PublishingSettingsSection';

interface IValidationErrors {
  title?: boolean;
  crmCompanyUuid?: boolean;
  categoryUuids?: boolean;
  shortDescriptionTitle?: boolean;
  shortDescriptionContent?: boolean;
  contactEmail?: boolean;
  heroImage?: boolean;
  activeFrom?: boolean;
}

interface IContentEditFormProps {
  contentEditState: any;
  validationErrors: IValidationErrors;
  isCreateMode?: boolean; // true when creating new content, false when editing
  updateTitle: (title: string) => void;
  // Main Info section update functions
  updateCrmCompanyUuid: (crmCompanyUuid: string | null) => void;
  updateCategoryUuid: (categoryUuids: string[]) => void;
  updateCategoryEnabled: (enabled: boolean) => void;
  updateDestination: (destinations: string[]) => void;
  updateProductName: (productName: string) => void;
  updateIsExclusive: (isExclusive: boolean) => void;
  updateProductTypeUuids: (productTypeUuids: string[]) => void;
  updateHeroImage: (heroImage: any) => void;
  addHeroImage: (file: File, displayName: string) => Promise<void>;
  // Content Details section update functions
  updateShortDescriptionTitle: (title: string) => void;
  updateShortDescriptionContent: (content: string) => void;
  addSection: () => void;
  removeSection: (sectionId: string) => void;
  updateSectionTitle: (sectionId: string, title: string) => void;
  updateSectionContent: (sectionId: string, content: string) => void;
  // Availability & Booking section update functions
  updateCountryCodes: (countryCodes: string[]) => void;
  resetCountryCodes: () => void;
  updateBookingStart: (date: string) => void;
  updateBookingEnd: (date: string) => void;
  updateStayPeriodStart: (date: string) => void;
  updateStayPeriodEnd: (date: string) => void;
  // Pricing Info section update functions
  updateCurrency: (currency: string) => void;
  updateFullPrice: (fullPrice: number | null) => void;
  updateDiscountedPrice: (discountedPrice: number | null) => void;
  updateDiscountPercent: (discountPercent: number | null) => void;
  // Media Assets section update functions
  addPhoto: (file: File, displayName: string) => Promise<void>;
  removePhoto: (index: number) => void;
  // Supporting Documents section update functions
  addDocument: (file: File, displayName: string) => Promise<void>;
  removeDocument: (index: number) => void;
  // Publishing Settings section update functions
  updateSaveAsDraft: (saveAsDraft: boolean) => void;
  updatePublishFor: (publishFor: "all_users" | "internal_users_only" | "external_users_only") => void;
  updateActiveFrom: (activeFrom: string) => void;
  updateActiveTo: (activeTo: string) => void;
  updateContactEmail: (contactEmail: string) => void;
}

export const ContentEditForm = ({
  contentEditState,
  validationErrors,
  isCreateMode = false,
  updateTitle,
  updateCrmCompanyUuid,
  updateCategoryUuid,
  updateCategoryEnabled,
  updateDestination,
  updateProductName,
  updateIsExclusive,
  updateProductTypeUuids,
  updateHeroImage,
  addHeroImage,
  updateShortDescriptionTitle,
  updateShortDescriptionContent,
  addSection,
  removeSection,
  updateSectionTitle,
  updateSectionContent,
  updateCountryCodes,
  resetCountryCodes,
  updateBookingStart,
  updateBookingEnd,
  updateStayPeriodStart,
  updateStayPeriodEnd,
  updateCurrency,
  updateFullPrice,
  updateDiscountedPrice,
  updateDiscountPercent,
  addPhoto,
  removePhoto,
  addDocument,
  removeDocument,
  updateSaveAsDraft,
  updatePublishFor,
  updateActiveFrom,
  updateActiveTo,
  updateContactEmail,
}: IContentEditFormProps) => {
  const [openSections, setOpenSections] = useState<string[]>(['content-body']);

  const handleSectionToggle = (sectionId: string, isOpen: boolean) => {
    setOpenSections(prev => (isOpen ? [...prev, sectionId] : prev.filter(id => id !== sectionId)));
  };

  return (
    <div className="content-edit-form space-y-10">
      {/* Main Info Section */}
      <MainInfoSection
        contentEditState={contentEditState}
        validationErrors={validationErrors}
        isCreateMode={isCreateMode}
        updateTitle={updateTitle}
        updateCrmCompanyUuid={updateCrmCompanyUuid}
        updateCategoryUuid={updateCategoryUuid}
        updateCategoryEnabled={updateCategoryEnabled}
        updateDestination={updateDestination}
        updateProductName={updateProductName}
        updateIsExclusive={updateIsExclusive}
        updateProductTypeUuids={updateProductTypeUuids}
        addHeroImage={addHeroImage}
      />

      {/* Content Details Section */}
      <ContentDetailsSection
        shortDescriptionTitle={contentEditState.shortDescription?.title || ''}
        shortDescriptionContent={contentEditState.shortDescription?.content || ''}
        sections={contentEditState.sections || []}
        validationErrors={validationErrors}
        updateShortDescriptionTitle={updateShortDescriptionTitle}
        updateShortDescriptionContent={updateShortDescriptionContent}
        addSection={addSection}
        removeSection={removeSection}
        updateSectionTitle={updateSectionTitle}
        updateSectionContent={updateSectionContent}
      />

      {/* Availability & Booking Section */}
      <AvailabilityBookingSection
        countryCodes={contentEditState.countryCodes || []}
        bookingStart={contentEditState.bookingStart || ''}
        bookingEnd={contentEditState.bookingEnd || ''}
        stayPeriodStart={contentEditState.stayPeriodStart || ''}
        stayPeriodEnd={contentEditState.stayPeriodEnd || ''}
        updateCountryCodes={updateCountryCodes}
        resetCountryCodes={resetCountryCodes}
        updateBookingStart={updateBookingStart}
        updateBookingEnd={updateBookingEnd}
        updateStayPeriodStart={updateStayPeriodStart}
        updateStayPeriodEnd={updateStayPeriodEnd}
      />

      {/* Pricing Info Section */}
      <PricingInfoSection
        currency={contentEditState.currency || ''}
        fullPrice={contentEditState.fullPrice}
        discountedPrice={contentEditState.discountedPrice}
        discountPercent={contentEditState.discountPercent}
        updateCurrency={updateCurrency}
        updateFullPrice={updateFullPrice}
        updateDiscountedPrice={updateDiscountedPrice}
        updateDiscountPercent={updateDiscountPercent}
      />

      {/* Media Assets Section */}
      <MediaAssetsSection
        uploads={contentEditState.photos || []}
        isCreateMode={isCreateMode}
        onAddPhoto={addPhoto}
        onDeletePhoto={removePhoto}
      />

      {/* Supporting Documents Section */}
      <SupportingDocumentsSection
        documents={contentEditState.documents || []}
        isCreateMode={isCreateMode}
        onAddDocument={addDocument}
        onDeleteDocument={removeDocument}
      />

      {/* Publishing Settings Section */}
      <PublishingSettingsSection
        saveAsDraft={contentEditState.saveAsDraft}
        publishFor={contentEditState.publishFor || 'all_users'}
        activeFrom={contentEditState.activeFrom || ''}
        activeTo={contentEditState.activeTo || ''}
        contactEmail={contentEditState.contactEmail || ''}
        validationErrors={validationErrors}
        updateSaveAsDraft={updateSaveAsDraft}
        updatePublishFor={updatePublishFor}
        updateActiveFrom={updateActiveFrom}
        updateActiveTo={updateActiveTo}
        updateContactEmail={updateContactEmail}
      />
    </div>
  );
};
